/**
 * Shop Filters Component - is used to filter products selection.
 */
import React, { useEffect, useState, useRef, Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import CheckboxList from './CheckboxList/CheckboxList';
import './CommonShopFilters.less';
import { handleToggleAction, handleOptionClick } from './helpers';

const CommonShopFilters = ({
  onFilterSelect,
  filterList,
  selectedFilters,
  setFilters,
  isDesktop,
  filterPosition,
}) => {
  const [openFilters, setOpenFilters] = useState({});
  const [showAll, setShowAll] = useState(true);
  // eslint-disable-next-line react/prop-types
  const { top: topSpace, bottom: bottomSpace } = filterPosition;
  const filterContainerRef = useRef(null);

  useEffect(() => {
    if (Object.keys(selectedFilters).length === 0) {
      setOpenFilters({ PRICE_RANGE: true });
    } else {
      setOpenFilters(selectedFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterMaxHeight = useCallback(() => {
    if (filterContainerRef.current) {
      const containerElement = filterContainerRef.current;
      const { offsetTop, scrollHeight, style } = containerElement;
      const parentOffsetHeight = containerElement.offsetParent.offsetHeight;
      const filterMaxHeight = window.innerHeight - topSpace - offsetTop - bottomSpace;
      style.maxHeight =
        scrollHeight > filterMaxHeight || parentOffsetHeight + topSpace > window.innerHeight
          ? `${filterMaxHeight}px`
          : 'none';
    }
  }, [bottomSpace, topSpace]);

  useEffect(() => {
    handleFilterMaxHeight();
    // When this file was converted to an error function and the rules of hooks
    // dependency, this was flagged as a warning.  But... the philosophy was to
    // make no changes to existing hooks, so this line gets disabled
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterList, filterContainerRef]);

  const showMoreShowLess = useCallback(
    (key) => {
      handleToggleAction(showAll, setShowAll, key);
    },
    [showAll]
  );

  const handleDropDownClick = useCallback(
    (key) => {
      handleToggleAction(openFilters, setOpenFilters, key);
    },
    [openFilters]
  );

  const handleOptionSelect = useCallback(
    (category, option) => {
      handleOptionClick(onFilterSelect, selectedFilters, setFilters, category, option);
    },
    [onFilterSelect, selectedFilters, setFilters]
  );

  const handleClear = useCallback(
    (filterLabel) => {
      const newStateObj = { ...selectedFilters };
      delete newStateObj[filterLabel];
      onFilterSelect(newStateObj);
      setFilters(newStateObj);
    },
    [onFilterSelect, selectedFilters, setFilters]
  );

  /**
   * Updates selected filters list
   * @param {string} filterName - name of the filter to make updates for (ex: COLOR)
   * @param {string} filterValue - name of the value to update filter with (ex: YELLOW)
   */

  return filterList.length ? (
    <div className="common-filter" ref={filterContainerRef}>
      {filterList.map(({ key, label, options }) => {
        const isRowExpanded = openFilters[key];
        const isOptionExpanded = showAll[key];
        const notPriceSectionBehaviour = !(key === 'PRICE_RANGE');
        const sortSection = key === 'SORT';
        return (
          <CheckboxList
            key={key}
            apiKeyName={key}
            title={label}
            list={options}
            selectedFilters={selectedFilters[key]}
            handleSetFilter={handleOptionSelect}
            handleClear={handleClear}
            isDesktop={isDesktop}
            handleDropDownClick={handleDropDownClick}
            isRowExpanded={isRowExpanded}
            notPriceSectionBehaviour={notPriceSectionBehaviour}
            showMoreShowLess={showMoreShowLess}
            sortSection={sortSection}
            isOptionExpanded={isOptionExpanded}
            transitionEnd={handleFilterMaxHeight}
          />
        );
      })}
    </div>
  ) : (
    <Fragment />
  );
};

CommonShopFilters.propTypes = {
  onFilterSelect: PropTypes.func.isRequired,
  filterList: PropTypes.arrayOf(PropTypes.shape({})),
  selectedFilters: PropTypes.shape({}),
  setFilters: PropTypes.func,
  isDesktop: PropTypes.bool,
  filterPosition: PropTypes.shape({}),
};

export default CommonShopFilters;
